import { createFileRoute } from "@tanstack/react-router"
import { Check, X, Zap, AlertCircle, ArrowRight } from "lucide-react"
import { Button } from "@/components/ui/button"
import { LifetimeAccessPricing } from "@/components/life-time-access-pricing"
import { createLLMOMeta, createBreadcrumbSchema, createFAQSchema, createArticleSchema } from "@/lib/seo"
import { useAuth } from "@/components/auth/AuthProvider"
import { useAnalytics } from "@/lib/analytics"
import { Paragraph, ContentSection } from "@/components/tools/SeoContent"
import { useState } from "react"
import { AskAIExplain } from "@/components/ask-ai-to-explain"

// ─── FAQ Data ────────────────────────────────────────────────
const faqs = [
  {
    question: "Is Zapify better than SuperProfile?",
    answer:
      "For Instagram-first creators, yes. Zapify offers AI-powered replies, brand voice controls, bulk DM automation, growth attribution, templates, smart offers, 10+ language support, and a possible $200 lifetime access offer. SuperProfile is a broader creator-commerce platform with storefronts, courses, webinars, and communities. If you just want Instagram growth, Zapify is more focused and effective.",
  },
  {
    question: "What is the best SuperProfile alternative?",
    answer:
      "Zapify is a strong SuperProfile alternative for creators who want AI-powered Instagram automation, bulk DMs, detailed insights, followers and leads gained through automation, ask-to-follow flows, email capture, templates, smart offers, and a focused Instagram growth workflow without creator-commerce bloat.",
  },
  {
    question: "Why switch from SuperProfile to Zapify?",
    answer:
      "Switch to Zapify if you want a focused Instagram growth platform instead of a broader creator-commerce suite. Zapify gives you AI replies, bulk DM workflows, growth attribution, smart offers, templates, language switching, 30-day free-plan value, and a possible lifetime access offer.",
  },
  {
    question: "Does Zapify have unlimited DMs?",
    answer:
      "Yes. Zapify's paid plans include unlimited DMs and unlimited automations. SuperProfile also promotes unlimited AutoDMs, but Zapify adds growth attribution, smart offers, and detailed insights that SuperProfile does not clearly offer.",
  },
  {
    question: "Does SuperProfile have unlimited AutoDM?",
    answer:
      "Yes. SuperProfile's public pricing promotes unlimited AutoDMs. The difference is that Zapify is positioned around Instagram growth attribution, smart offers, templates, language switching, and lifetime-access economics rather than broader creator-commerce features.",
  },
  {
    question: "Does Zapify have AI replies?",
    answer:
      "Yes. Zapify is an AI-powered Instagram comment management and automation platform with AI replies and brand voice controls that match your tone, length, and style.",
  },
  {
    question: "How does Zapify pricing compare with SuperProfile?",
    answer:
      "Zapify has a $0 free plan for 30 days or 1K contacts, paid plans at $9.99/month and $19.99/month, and a limited $200 lifetime access offer. SuperProfile lists Starter Free Forever, Creator at ₹99 first month then ₹499, and a custom Pro plan. Zapify has the cleaner value story for Instagram-focused automation.",
  },
  {
    question: "Who should still consider SuperProfile?",
    answer:
      "SuperProfile may make sense if you specifically want a creator-commerce suite with link-in-bio storefronts, digital product selling, courses, webinars, Telegram/Discord communities, integrations, or dedicated growth/ads services. For most Instagram-first creators who want growth attribution and smart campaign setup, Zapify is the better fit.",
  },
  {
    question: "Can I switch from SuperProfile to Zapify?",
    answer:
      "Yes. If your main workflows are Instagram comment-to-DM, Story DMs, bulk DMs, AI replies, lead capture, ask-to-follow, templates, smart offers, and follower growth, Zapify is a good switch. Before switching, check whether you rely on SuperProfile-specific features like storefronts, digital products, courses, webinars, or communities.",
  },
]

// ─── Feature Table Data ──────────────────────────────────────
type FeatureRow = {
  name: string
  zapify: string | boolean
  superprofile: string | boolean
  zapifyNote?: string
  superprofileNote?: string
}

type FeatureCategory = {
  category: string
  rows: FeatureRow[]
}

const featureCategories: FeatureCategory[] = [
  {
    category: "Instagram automation",
    rows: [
      { name: "Instagram comment-to-DM automation", zapify: true, superprofile: true, zapifyNote: "Personalized DMs with links, offers, and content" },
      { name: "Bulk DM automation", zapify: true, superprofile: true, zapifyNote: "Unlimited DMs on paid plans" },
      { name: "Unlimited DMs on paid plans", zapify: true, superprofile: true },
      { name: "Post and Reel automation", zapify: true, superprofile: true },
      { name: "Multiple selected posts", zapify: true, superprofile: false, superprofileNote: "Not publicly confirmed" },
      { name: "Any-post automation", zapify: true, superprofile: false, zapifyNote: "Evergreen automation", superprofileNote: "Verify parity" },
      { name: "Story DM automation", zapify: true, superprofile: true, superprofileNote: "Story Auto-Reply listed" },
      { name: "IG Live auto-reply", zapify: false, superprofile: true, zapifyNote: "Not publicly confirmed" },
    ],
  },
  {
    category: "AI, growth, and conversion",
    rows: [
      { name: "AI-powered comment replies", zapify: true, superprofile: false, zapifyNote: "Built-in contextual AI", superprofileNote: "Personalized DMs; verify AI parity" },
      { name: "Brand voice controls", zapify: true, superprofile: false, zapifyNote: "Tone, length, style", superprofileNote: "Not clearly promoted" },
      { name: "Ask-to-follow before sharing content", zapify: true, superprofile: true, zapifyNote: "Native growth flow", superprofileNote: "Follower Growth Tool listed" },
      { name: "Email capture in DMs", zapify: true, superprofile: true },
      { name: "Detailed Instagram Reel insights", zapify: true, superprofile: false, superprofileNote: "No public equivalent" },
      { name: "Followers gained attribution", zapify: true, superprofile: false, superprofileNote: "No public equivalent" },
      { name: "Leads gained attribution", zapify: true, superprofile: false, superprofileNote: "No public equivalent" },
    ],
  },
  {
    category: "Setup, accounts, and business model",
    rows: [
      { name: "Templates for quick setup", zapify: true, superprofile: false, zapifyNote: "Launch in minutes", superprofileNote: "Not clearly promoted" },
      { name: "Smart offers / link cards in DMs", zapify: true, superprofile: false, zapifyNote: "Quick campaign setup", superprofileNote: "Lead magnets exist; not Zapify-style" },
      { name: "Multiple Instagram accounts", zapify: true, superprofile: true, zapifyNote: "Up to 6 profiles", superprofileNote: "Up to 5 on Creator" },
      { name: "Team access", zapify: true, superprofile: false, zapifyNote: "From Starter", superprofileNote: "Not clearly shown" },
      { name: "10+ language switching", zapify: true, superprofile: false, superprofileNote: "Not publicly confirmed" },
      { name: "Lifetime access option", zapify: true, superprofile: false, zapifyNote: "$200 one-time" },
      { name: "Link-in-bio storefront", zapify: false, superprofile: true, zapifyNote: "Not core Zapify positioning" },
      { name: "Digital products / courses / webinars", zapify: false, superprofile: true, zapifyNote: "Not core Zapify positioning" },
      { name: "Dedicated growth/ads manager", zapify: false, superprofile: true, zapifyNote: "Not core Zapify positioning", superprofileNote: "Pro custom plan" },
      { name: "Meta approval / partner status", zapify: true, superprofile: true, zapifyNote: "Approved by Meta", superprofileNote: "Positions as safe/compliant" },
    ],
  },
]

// ─── Pricing Data ────────────────────────────────────────────
const pricingTiers = [
  { label: "Free / trial", zapify: "$0 for 30 days", superprofile: "$0 Forever (10 leads)", note: "Zapify: 1K contacts, no lead limit during trial" },
  { label: "Entry paid plan", zapify: "$9.99/mo", superprofile: "₹99 first mo, then ₹499", note: "Zapify: clear flat pricing in USD" },
  { label: "Professional", zapify: "$19.99/mo", superprofile: "Custom Pro pricing", note: "Zapify: transparent, no custom quotes" },
  { label: "Lifetime access", zapify: "$200 once", superprofile: "Not available", note: "Zapify: no recurring fees ever" },
]

const problemCards = [
  {
    title: "SuperProfile is a broad creator-commerce platform",
    body:
      "Storefronts, courses, webinars, communities, digital products, integrations - useful if you need all that. But if your goal is Instagram DM automation and growth, it is more platform than you need.",
  },
  {
    title: "SuperProfile lacks focused growth attribution",
    body:
      "SuperProfile can collect leads, but it does not publicly show you which Instagram automations grew your followers or captured leads. Zapify tracks what the automation produced.",
  },
  {
    title: "SuperProfile pricing changes after the first month",
    body:
      "SuperProfile's Creator plan starts at ₹99 for the first month, then jumps to ₹499. Pro is custom pricing. Zapify's plans are flat and transparent from day one.",
  },
]

const chooseZapifyIf = [
  "Want a focused Instagram growth platform - not a creator-commerce suite",
  "Want AI-powered replies with brand voice controls",
  "Want unlimited DMs on paid plans with flat pricing",
  "Want to know which campaigns grew your followers and captured leads",
  "Want templates and smart offers for faster campaign setup",
  "Want detailed Reel/post insights and growth attribution",
  "Want 10+ language support for global audiences",
  "Want transparent pricing that does not change after the first month",
  "Want to lock in lifetime access for $200 instead of paying recurring fees",
]

const chooseSuperProfileIf = [
  "Need a link-in-bio storefront bundled with AutoDM",
  "Need built-in digital product selling",
  "Need course, webinar, or community monetization tools",
  "Need dedicated growth/ads manager on a custom Pro plan",
  "Need third-party integrations like Zapier, Kit, or Mailchimp from one suite",
  "Need a free-forever plan with limited lead magnet capture",
]

const differentiators = [
  {
    num: "01",
    title: "Focused on Instagram growth, not everything",
    body:
      "SuperProfile is a broad creator-commerce platform. Zapify is focused on one thing: turning Instagram engagement into followers, leads, and sales. Less platform bloat, more growth results.",
  },
  {
    num: "02",
    title: "Growth attribution that shows what worked",
    body:
      "Zapify tracks followers gained and leads captured through automation. SuperProfile can collect leads, but Zapify shows you which specific campaigns grew your account.",
  },
  {
    num: "03",
    title: "AI replies with brand voice",
    body:
      "Zapify's AI understands context and matches your tone, length, and style. Replies feel personal and relevant. SuperProfile's AutoDM mentions personalized DMs but does not clearly promote Zapify-style brand voice controls.",
  },
  {
    num: "04",
    title: "Smart offers and templates",
    body:
      "Launch campaigns in minutes with templates and smart offers. Send a free resource, product page, booking link, coupon, or lead magnet without rebuilding from scratch every time.",
  },
  {
    num: "05",
    title: "Transparent, flat pricing",
    body:
      "Zapify's plans are clear: $0 free for 30 days, $9.99/month Starter, $19.99/month Professional, or $200 lifetime. No first-month teaser pricing. No custom quotes for basic features.",
  },
  {
    num: "06",
    title: "Lifetime access for $200",
    body:
      "SuperProfile has no public lifetime offer. Zapify gives creators the option to pay once and use the platform forever. For creators building long-term, that is significantly better economics.",
  },
]

// ─── Route ───────────────────────────────────────────────────
export const Route = createFileRoute("/comparison/superprofile-vs-zapify-pro")({
  head: () => ({
    meta: [
      ...createLLMOMeta({
        title: "SuperProfile vs Zapify: Best SuperProfile AutoDM Alternative",
        description:
          "Compare SuperProfile vs Zapify for Instagram DM automation, AI replies, bulk DMs, Story DMs, pricing, insights, and creator growth. See why creators choose Zapify.",
        url: "https://zapify.pro/comparison/superprofile-vs-zapify-pro",
        publishedTime: "2026-06-06",
        modifiedTime: "2026-06-06",
        ogType: "article",
      }),
      { property: "og:image", content: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png" },
      { property: "og:locale", content: "en_US" },
      { name: "keywords", content: "SuperProfile vs Zapify, SuperProfile alternative, SuperProfile AutoDM alternative, Zapify vs SuperProfile, Instagram DM automation tool, Instagram comment to DM automation, bulk Instagram DM automation, AI Instagram replies" },
    ],
    links: [
      { rel: "canonical", href: "https://zapify.pro/comparison/superprofile-vs-zapify-pro" },
    ],
    scripts: [
      createBreadcrumbSchema({
        itemListElement: [
          { position: 1, name: "Home", item: "https://zapify.pro" },
          { position: 2, name: "Comparisons", item: "https://zapify.pro/comparison" },
          { position: 3, name: "SuperProfile vs Zapify", item: "https://zapify.pro/comparison/superprofile-vs-zapify-pro" },
        ],
      }),
      createFAQSchema(faqs),
      createArticleSchema({
        headline: "SuperProfile vs Zapify: Best SuperProfile AutoDM Alternative",
        description: "A practical comparison of SuperProfile and Zapify for Instagram DM automation, bulk DMs, AI replies, Story DMs, pricing, and creator growth.",
        image: "https://cms.zapify.pro/uploads/OG_image1_f82f5871e2.png",
        author: { name: "Zapify", url: "https://zapify.pro" },
        datePublished: "2026-06-06",
        dateModified: "2026-06-06",
        publisher: {
          name: "Zapify Pro",
          url: "https://zapify.pro",
          logo: "https://zapify.pro/logo-small.png",
          description: "AI-Powered Instagram Comment Management & Automation",
        },
      }),
    ],
  }),
  component: SuperProfileComparisonPage,
})

// ─── Page Component ──────────────────────────────────────────
export function SuperProfileComparisonPage() {
  const { track } = useAnalytics()
  const { openAuth } = useAuth()
  const [openFaq, setOpenFaq] = useState(0)

  const handleCTA = (ctaId: string, buttonText: string) => {
    track("cta_clicked", { cta_id: ctaId, button_text: buttonText })
    openAuth()
  }

  return (
    <div className="min-h-screen relative overflow-hidden bg-white dark:bg-black transition-colors duration-300">
      <div className="absolute inset-0 bg-white/50 dark:bg-black/20 [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)] pointer-events-none" />
      <div className="absolute inset-0 bg-[radial-gradient(circle,#80808015_1px,transparent_1px)] bg-[size:20px_20px] pointer-events-none" />

      <div className="relative z-10 pt-28 sm:pt-36 pb-12">
        {/* Section 1: Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 text-center mb-16 sm:mb-20">
          <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-white dark:bg-neutral-900 border border-neutral-100 dark:border-neutral-800 shadow-sm mb-6">
            <span className="text-neutral-600 dark:text-neutral-300 font-semibold text-xs">Updated June 2026</span>
          </div>

          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight max-w-5xl mx-auto">
            <span className="gradient-text">Zapify vs</span> SuperProfile
          </h1>
          <p className="text-xl sm:text-2xl md:text-3xl font-bold text-neutral-900 dark:text-white max-w-4xl mx-auto mb-5 tracking-tight">
            SuperProfile is broad. Zapify is focused on Instagram growth.
          </p>
          <p className="text-base sm:text-lg md:text-xl text-neutral-600 dark:text-neutral-400 font-medium max-w-3xl mx-auto mb-8 sm:mb-10">
            Zapify gives you AI-powered replies, growth attribution, smart offers, and unlimited DMs. No storefronts, courses, or webinars to wade through.
          </p>

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <Button
              onClick={() => handleCTA("CTA_SuperProfile_Hero", "Start Free 30 Days")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-8 py-6 text-base rounded-full shadow-lg"
            >
              Start Free 30 Days
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              className="px-8 py-6 text-base rounded-full font-semibold border-neutral-200 dark:border-neutral-700"
              onClick={() => {
                document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth" })
              }}
            >
              See Pricing
            </Button>
          </div>
        </section>

        {/* Ask AI to Explain */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <AskAIExplain
            context="comparison"
            meta={{ competitorName: "SuperProfile", pageUrl: "https://zapify.pro/comparison/superprofile-vs-zapify-pro" }}
            isCard
            isWidget
          />
        </section>

        {/* Section 2: Quick Answer Box */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20" aria-label="Featured summary">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 shadow-sm">
            <p className="text-xs font-bold uppercase tracking-widest text-brand-button mb-3">
              Bottom line - which is better, Zapify or SuperProfile?
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed mb-4">
              <strong className="text-neutral-900 dark:text-white">Zapify is the better choice for Instagram creators who want focused automation and growth.</strong>{" "}
              SuperProfile is a broad creator-commerce platform with AutoDM, storefronts, courses, webinars, and communities. That breadth adds complexity if your real goal is Instagram DM automation.
            </p>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed">
              Choose Zapify if you want <strong className="gradient-text">AI replies</strong>, growth attribution, smart offers, templates, and flat transparent pricing. SuperProfile is worth considering only if you need storefronts, digital products, courses, or dedicated growth/ads services.
            </p>
          </article>
        </section>

        {/* Section 3: Problem With SuperProfile */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              The problem with <span className="gradient-text">SuperProfile</span> for Instagram growth
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 max-w-3xl mx-auto mt-3">
              SuperProfile does many things. For creators who just want Instagram automation to work, that breadth creates unnecessary complexity.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            {problemCards.map((item) => (
              <article key={item.title} className="rounded-xl border border-red-200 dark:border-red-800/40 bg-red-50/50 dark:bg-red-900/10 p-5 sm:p-6">
                <div className="w-9 h-9 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
                  <X className="w-4 h-4 text-red-500 dark:text-red-400" strokeWidth={3} />
                </div>
                <h3 className="text-base font-bold text-neutral-900 dark:text-white mb-2">{item.title}</h3>
                <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.body}</p>
              </article>
            ))}
          </div>
        </section>

        {/* Section 4: Choose Tool */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="rounded-2xl border-2 border-green-200 dark:border-green-800/50 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                  <Zap className="w-5 h-5 text-green-600 dark:text-green-400" />
                </div>
                <h2 className="text-lg font-bold text-green-600 dark:text-green-400">Choose Zapify if you…</h2>
              </div>
              <ul className="space-y-2.5">
                {chooseZapifyIf.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm text-neutral-700 dark:text-neutral-300 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="rounded-2xl border-2 border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900/50 p-6 sm:p-8">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-neutral-500 dark:text-neutral-400" />
                </div>
                <h2 className="text-lg font-bold text-neutral-600 dark:text-neutral-400">Choose SuperProfile only if you…</h2>
              </div>
              <ul className="space-y-2.5">
                {chooseSuperProfileIf.map((item) => (
                  <li key={item} className="flex items-start gap-2.5">
                    <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-neutral-100 text-neutral-500 dark:bg-neutral-800 dark:text-neutral-400">
                      <Check className="w-3 h-3" strokeWidth={3} />
                    </span>
                    <span className="text-sm text-neutral-600 dark:text-neutral-400 leading-snug">{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Section 5: Feature Comparison Table */}
        <section id="feature-table" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              How does Zapify compare to SuperProfile feature{" "}
              <span className="gradient-text">by feature?</span>
            </h2>
            <Paragraph className="max-w-3xl mx-auto">
              A transparent, side-by-side breakdown of every Instagram automation capability that matters in 2026.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 overflow-hidden shadow-sm bg-white dark:bg-neutral-900/50">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[40%]">Feature</th>
                    <th className="text-left px-4 sm:px-6 py-4 uppercase tracking-wider text-xs w-[30%] text-green-700 dark:text-green-300">Zapify</th>
                    <th className="text-left px-4 sm:px-6 py-4 font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider text-xs w-[30%]">SuperProfile</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-neutral-100 dark:divide-neutral-800">
                  {featureCategories.flatMap((cat) => [
                    <tr key={`cat-${cat.category}`} className="bg-neutral-50/70 dark:bg-neutral-900/70">
                      <td colSpan={3} className="px-4 sm:px-6 py-2.5 text-xs font-bold uppercase tracking-widest text-brand-button">{cat.category}</td>
                    </tr>,
                    ...cat.rows.map((row) => (
                      <tr key={`${cat.category}-${row.name}`} className="hover:bg-neutral-50/50 dark:hover:bg-neutral-800/20 transition-colors align-top">
                        <td className="px-4 sm:px-6 py-3.5 font-medium text-neutral-700 dark:text-neutral-300">{row.name}</td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-800 dark:text-neutral-100">{typeof row.zapify === "boolean" ? (row.zapify ? "Yes" : "No") : row.zapify}</span>
                            {row.zapifyNote ? <span className="text-xs text-green-700 dark:text-green-300">{row.zapifyNote}</span> : null}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-3.5">
                          <div className="flex flex-col gap-1.5">
                            <span className="text-sm font-semibold text-neutral-700 dark:text-neutral-200">{typeof row.superprofile === "boolean" ? (row.superprofile ? "Yes" : "No") : row.superprofile}</span>
                            {row.superprofileNote ? <span className="text-xs text-red-600 dark:text-red-300">{row.superprofileNote}</span> : null}
                          </div>
                        </td>
                      </tr>
                    )),
                  ])}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Section 6: Pricing Breakdown */}
        <section id="pricing" className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              Zapify vs SuperProfile <span className="gradient-text">pricing</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Transparent flat pricing versus creator-commerce suite pricing that changes after the first month.
            </Paragraph>
          </div>

          <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 overflow-hidden">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-neutral-50/80 dark:bg-neutral-900/80 border-b border-neutral-200 dark:border-neutral-800">
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Plan stage</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-green-700 dark:text-green-300">Zapify</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-600 dark:text-neutral-300">SuperProfile</th>
                  <th className="text-left px-4 sm:px-5 py-3 text-xs font-semibold uppercase tracking-wide text-neutral-500 dark:text-neutral-400">Why it matters</th>
                </tr>
              </thead>
              <tbody>
                {pricingTiers.map((tier) => (
                  <tr key={tier.label} className="align-top border-b last:border-b-0 border-neutral-100 dark:border-neutral-800/80">
                    <td className="px-4 sm:px-5 py-4 text-sm font-semibold text-neutral-800 dark:text-neutral-100">{tier.label}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-green-700 dark:text-green-300">{tier.zapify}</td>
                    <td className="px-4 sm:px-5 py-4 text-lg font-black tracking-tight text-neutral-700 dark:text-neutral-100">{tier.superprofile}</td>
                    <td className="px-4 sm:px-5 py-4"><span className="text-xs sm:text-sm text-green-700 dark:text-green-300 font-semibold">{tier.note}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-8">
            <LifetimeAccessPricing
              spotsLeft={87}
              totalSpots={100}
              price={200}
              originalMonthlyPrice={39}
              onGetAccess={() => handleCTA("CTA_SuperProfile_Lifetime_Pricing", "Claim Lifetime Access")}
            />
          </div>
        </section>

        {/* Section 7: Why Zapify Wins */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <div className="text-center mb-10 sm:mb-12">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight">
              What makes Zapify the better <span className="gradient-text">growth tool</span>
            </h2>
            <Paragraph className="max-w-2xl mx-auto">
              Six reasons Instagram-first creators choose Zapify over SuperProfile.
            </Paragraph>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            {differentiators.map((item) => (
              <article key={item.num} className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-5 sm:p-6">
                <div className="flex items-start gap-4">
                  <span className="text-2xl font-black text-brand-button/30 dark:text-brand-button/20 leading-none shrink-0">{item.num}</span>
                  <div>
                    <h3 className="font-bold text-neutral-900 dark:text-white text-sm mb-1.5">{item.title}</h3>
                    <p className="text-sm text-neutral-600 dark:text-neutral-400 leading-relaxed">{item.body}</p>
                  </div>
                </div>
              </article>
            ))}
          </div>
        </section>

        {/* Section 8: Honest Verdict */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 mb-16 sm:mb-20">
          <article className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 p-6 sm:p-8 text-center">
            <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-neutral-900 dark:text-white tracking-tight mb-4">
              Honest verdict: <span className="gradient-text">Zapify wins</span>
            </h2>
            <p className="text-sm sm:text-base text-neutral-600 dark:text-neutral-400 leading-relaxed max-w-4xl mx-auto">
              SuperProfile is a capable creator-commerce platform, but most Instagram creators do not need storefronts, courses, webinars, or dedicated ads managers. Zapify is simpler, more focused, and built around the growth outcomes creators care about: more followers, more leads, smarter AI replies, and clear attribution.
            </p>
          </article>
        </section>

        {/* Section 9: FAQ */}
        <section className="w-full py-12 sm:py-20 px-4 sm:px-6 border-b border-neutral-100 dark:border-white/5">
          <div className="max-w-7xl mx-auto">
            <div className="mb-8 text-center sm:mb-12">
              <h2 className="text-2xl sm:text-3xl md:text-5xl lg:text-6xl font-bold text-[#181C31] dark:text-white tracking-tight">
                Frequently <span className="gradient-text">Asked Questions</span>
              </h2>
              <p className="text-sm sm:text-base md:text-xl text-[#64657E] dark:text-gray-400 font-medium max-w-2xl mx-auto mt-3 sm:mt-5">
                Everything you need to know about SuperProfile vs Zapify
              </p>
            </div>

            <div className="mx-auto max-w-5xl space-y-3">
              {faqs.map((faq, index) => {
                const isOpen = openFaq === index
                return (
                  <div key={faq.question} className="rounded-2xl border border-border/50 bg-card/90 px-4 py-3.5 sm:px-6 sm:py-4 dark:bg-card/80">
                    <button type="button" onClick={() => setOpenFaq((prev) => (prev === index ? -1 : index))} className="flex w-full items-center justify-between gap-3 text-left">
                      <div className="flex items-center gap-3">
                        <span className="text-base leading-none">{["⚡", "💸", "🤖", "🔄", "📩", "🛡️", "🧠", "💬", "🎯"][index] ?? "⚡"}</span>
                        <p className="text-base font-semibold leading-snug text-foreground sm:text-[1.15rem]">{faq.question}</p>
                      </div>
                      <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-full text-muted-foreground">{isOpen ? "−" : "+"}</span>
                    </button>
                    <div className={`grid transition-all duration-300 ${isOpen ? "mt-2.5 grid-rows-[1fr] opacity-100 sm:mt-3" : "grid-rows-[0fr] opacity-0"}`}>
                      <p className="overflow-hidden pr-3 text-[13px] leading-relaxed text-muted-foreground sm:pr-8 sm:text-[0.95rem]">{faq.answer}</p>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </section>

        {/* Section 10: Other Comparisons */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16 border-b border-neutral-100 dark:border-white/5">
          <div className="text-center mb-8">
            <h2 className="text-xl sm:text-2xl font-bold text-neutral-900 dark:text-white">
              Compare Zapify vs <span className="gradient-text">other tools</span>
            </h2>
          </div>
          <div className="flex flex-wrap justify-center gap-3">
            {[
              { label: "Zapify vs ManyChat", href: "/comparison/manychat-vs-zapify-pro" },
              { label: "Zapify vs LinkDM", href: "/comparison/linkdm-vs-zapify-pro" },
              { label: "Zapify vs Zorcha", href: "/comparison/zorcha-vs-zapify-pro" },
              { label: "Zapify vs InstantDM", href: "/comparison/instantdm-vs-zapify-pro" },
              { label: "Zapify vs ReplyRush", href: "/comparison/replyrush-vs-zapify-pro" },
            ].map((link) => (
              <a key={link.href} href={link.href} className="px-5 py-2.5 rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900/50 text-sm font-semibold text-neutral-700 dark:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-700 hover:bg-neutral-50 dark:hover:bg-neutral-800/50 transition-all duration-200">
                {link.label}
              </a>
            ))}
          </div>
        </section>

        {/* Section 11: Bottom CTA */}
        <section className="relative overflow-hidden py-16 sm:py-24 px-4 sm:px-6">
          <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] bg-brand-accent/10 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-2xl mx-auto text-center">
            <h2 className="text-2xl sm:text-4xl md:text-5xl font-bold text-neutral-900 dark:text-white mb-4 sm:mb-6 tracking-tight">
              Focused automation. <span className="gradient-text">Real growth.</span>
            </h2>
            <p className="text-base sm:text-lg text-neutral-600 dark:text-neutral-400 mb-8 sm:mb-10 max-w-xl mx-auto">
              Start free for 30 days, launch your first automation in minutes, and grow your Instagram without creator-platform bloat.
            </p>
            <Button
              onClick={() => handleCTA("CTA_Comparison_Bottom", "Start Free with Zapify")}
              className="bg-primary-gradient text-primary-foreground hover:brightness-[0.97] active:brightness-[0.92] font-bold px-10 py-6 text-lg rounded-full shadow-lg"
            >
              Start Free with Zapify
              <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
            <p className="mt-4 text-sm text-neutral-500 dark:text-neutral-400">
              30-day free plan &middot; No credit card &middot; 1-minute setup
            </p>
          </div>
        </section>

        {/* Disclaimer */}
        <ContentSection className="max-w-7xl mx-auto px-4 sm:px-6 pb-8">
          <p className="text-xs text-neutral-400 dark:text-neutral-500 text-center">
            SuperProfile is a trademark of its respective owner. Zapify is not affiliated with, endorsed by, or sponsored by SuperProfile. Feature and pricing information is based on publicly available information and Zapify research as of June 6, 2026. Always check each provider's website for current pricing and plan details.
          </p>
        </ContentSection>
      </div>
    </div>
  )
}
